import * as React from 'react';
import { Row, Col } from 'antd';
import { observer } from 'mobx-react';

import { CardController } from 'src/app/shared/card.controller';
import { BooleanView } from 'src/core/ui/boolean.view';
import { DateView } from 'src/core/ui/date.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { TextView } from 'src/core/ui/text.view';
import { TaxonomyTagsEditorController } from 'src/app/general/taxonomies/parts/taxonomy-tags-editor.controller';
import { ZuluIcon } from 'src/app/shared/icon';

interface IExtendedProperty {
	name: string;
	label: string;
	model: any;
	onChange: (value: any) => void;
	required: boolean;
	schema: JsonSchema;
}

interface Props {
	title: string;
	icon?: string;
	mode: ControlMode;
	model: { [prop: string]: any };
	validTaxonomies?: string[];
	schema: JsonSchema;
	columns: number;
	setBusy: (isBusy: boolean) => void;
}

@observer
export class ExtendedSchemaView extends React.Component<Props, {}> {
	constructor(props: Readonly<Props>) {
		super(props);
	}

	private getExtendedProperties(model: any, schema: JsonSchema): IExtendedProperty[] {
		const { properties, required } = schema.allOf[0];

		return Object.keys(properties)
			.map((propertyName) => {
				const jsc = properties[propertyName] as JsonSchema;
				return {
					name: propertyName,
					label: jsc.title,
					model: model[propertyName],
					onChange: (value: any) => model[propertyName] = value,
					required: required.some((p) => p === propertyName),
					schema: jsc
				};
			});
	}

	private getPropetyView(property: IExtendedProperty) {
		const { model, schema } = property;
		const props = {
			key: property.name,
			label: property.label,
			required: property.required
		};

		if (schema.type == 'boolean') {
			return (
				<BooleanView
					value={model}
					mode={this.props.mode}
					onChanged={property.onChange}
					{...props}
				/>
			);
		} else if (schema.type == 'string') {
			if (schema.format == 'date-time') {
				return (
					<DateView
						value={model}
						mode={this.props.mode}
						format="small-date"
						onChanged={property.onChange}
						dateUsage="utc"
						{...props}
					/>
				);
			} else if (schema.format == 'email') {
				return (
					<TextView
						value={model}
						addonBefore={(<ZuluIcon type="mail" />)}
						mode={this.props.mode}
						onChanged={property.onChange}
						{...props}
					/>
				);
			} else if (schema.format == 'uri') {
				return (
					<TextView
						value={model}
						addonBefore="http://"
						mode={this.props.mode}
						onChanged={property.onChange}
						{...props}
					/>
				);
			} else if (Array.isArray(schema.enum) && schema.enum.length > 0) {
				const options = schema.enum.map((value) => ({
					value: value,
					label: value
				}));
				return (
					<DropdownView
						model={model}
						disabled={this.props.mode === 'display'}
						options={options}
						onChanged={(newValue) => {
							property.onChange(newValue != null ? newValue.value : null);
						}}
						{...props}
					/>
				);
			} else {
				return (
					<TextView
						value={model}
						mode={this.props.mode}
						onChanged={property.onChange}
						{...props}
					/>
				);
			}
		} else if (this.isTaxonomy(schema)) {
			let taxonomy: string;
			let multi: boolean;
			const allowNewTags = schema._config.allowNewTags;
			if (schema.$ref != null) {
				taxonomy = schema.$ref;
				multi = false;
			} else {
				taxonomy = schema.items.$ref;
				multi = true;
			}

			return (
				<TaxonomyTagsEditorController
					taxonomyDefinitionName={taxonomy}
					value={model}
					multi={multi}
					allowNewTags={allowNewTags}
					onChange={(newValue) => {
						if (multi) {
							const tags = (newValue as TaxonomyTagSummary[]).map(m => m.id);
							property.onChange(tags);
						} else {
							const tag = newValue == null ? newValue : (newValue as TaxonomyTagSummary).id;
							property.onChange(tag);
						}
					}}
					mode={this.props.mode}
					setBusy={this.props.setBusy}
					{...props}
				/>
			);
		}
	}

	private isTaxonomy(schema: JsonSchema): boolean {
		return schema.$ref != null || (schema.items != null && schema.items.$ref != null);
	}

	render(): React.ReactNode {
		const { model, schema, validTaxonomies } = this.props;
		if (model == null || schema == null || schema.allOf == null || Object.getOwnPropertyNames(this.props.schema.allOf[0].properties).length === 0) {
			return null;
		}

		let { columns, icon, title, mode } = this.props;
		icon = icon || 'book';
		columns = columns || 1;

		let properties = this.getExtendedProperties(model, schema);
		if (validTaxonomies != null) {
			properties = properties.filter(m => !this.isTaxonomy(m.schema) || validTaxonomies.some(p => p === m.name));
		}

		const propertyViews = properties
			.map((property) => this.getPropetyView(property));
		const rows = propertyViews.length > columns ? Math.ceil(propertyViews.length / columns) : 1;

		const colSize = Math.floor(24 / columns);
		const content = new Array(rows);
		for (let i = 0; i < rows; i++) {
			content[i] = (
				<Row gutter={16} key={i.toString()}>
					{
						propertyViews
							.slice(columns * i, columns * (i + 1))
							.map((m, index) => (
								<Col xs={colSize} key={index}>
									{m}
								</Col>
							))
					}
				</Row>
			);
		}

		return (
			<CardController
				className="card-borderless"
				title={title}
				icon={icon}
				mode={mode}>
				{content}
			</CardController>
		);
	}
}



// WEBPACK FOOTER //
// ./src/core/ui/extended-schema.view.tsx